import { inject, Injectable } from '@angular/core';
import { ConsensusReconciliationSseEventPayload } from '@api-types/sse/event-payloads/consensus-reconciliation.sse-event-payload';
import { SseEventType } from '@api-types/sse/sse.types';
import { SseEventHandler } from '@core/sse/sse-event-handler';
import { ConsensusGridStateService } from './consensus-grid-state.service';
import { ConsensusValuesStateService } from './consensus-values-state.service/consensus-values-state.service';

@Injectable()
export class ConsensusReconciliationSseHandler implements SseEventHandler<
  typeof SseEventType.ConsensusReconciliation
> {
  readonly eventType = SseEventType.ConsensusReconciliation;

  private readonly consensusGridStateService = inject(
    ConsensusGridStateService,
  );
  private readonly consensusValuesStateService = inject(
    ConsensusValuesStateService,
  );

  /** Months completed in the current wave, drained when the last one lands. */
  private readonly completedMonths = new Set<string>();

  handle(payload: ConsensusReconciliationSseEventPayload): void {
    if (payload.isReconciling) {
      this.consensusGridStateService.addReconcilingScope(
        payload.scope,
        payload.consensusColumnId,
        payload.dateValue,
      );
      return;
    }

    // worker is done. flip cells from spinner → dim before kicking off the
    // refetch, so the user never sees the stale cached value uncovered. the
    // dim state is keyed by (columnId, dateValue) so a COMPLETED event for
    // one month does not clear the dim state of another concurrent job. it
    // clears in `clearPendingRefreshOnIdle` once the whole refresh wave has
    // gone idle — i.e. every open block (including the slow one carrying the
    // edited rows) has landed, never before.
    this.consensusGridStateService.markScopeCompletedAwaitingRefresh(
      payload.scope,
      payload.consensusColumnId,
      payload.dateValue,
    );
    // the edited month's values are now stale in the lazy value cache (a leaf
    // edit cascades into every ancestor SUM for this month) — evict it so the
    // refetch below reloads fresh values instead of serving the cache. Evicted
    // per event, not per wave: a lost event then costs one stale month rather
    // than every month the wave touched.
    this.consensusValuesStateService.evictMonths(
      [payload.dateValue],
      [payload.scope],
    );
    this.completedMonths.add(payload.dateValue);

    // One refresh per wave, not per month. The backend emits a COMPLETED event
    // per month, and each refresh refetches every open block — a multi-month
    // reconcile rebuilt the whole grid once per month (measured: six months →
    // six waves, twelve dimension-groups round trips, ~25s of churn). The
    // scope map is emptied by markScopeCompletedAwaitingRefresh above, and
    // both the `completed` and `failed` queue events send isReconciling:false,
    // so it always drains.
    if (
      this.consensusGridStateService.reconcilingScopesByColumnAndMonth().size >
      0
    ) {
      return;
    }

    const months = [...this.completedMonths];
    this.completedMonths.clear();
    void this.consensusGridStateService.refreshAfterReconciliation(months);
  }
}
